import _ from 'lodash';

import { fixedTwoFractionDigitsExceptIntegerCurrencyOptions } from 'constants/loanApplication';
import { DEFAULT_DECIMAL_SCALE } from 'constants/number';
import { checkIsHelocLoanType } from 'utils/loanApplication';
import { formatNumberToCurrencyString } from 'utils/number';
import { getCombinedPendingChangeKind, getModifiedFieldChangeKind } from 'utils/pendingChangeKind';

export const formatCurrency = amount =>
  formatNumberToCurrencyString(amount ?? 0, fixedTwoFractionDigitsExceptIntegerCurrencyOptions);

export const getLoanAmountValue = metrics => {
  const { totalLoanAmount, helocDrawAmount, loanTypeValue } = metrics ?? {};
  const isHeloc = checkIsHelocLoanType(loanTypeValue);

  if (isHeloc && helocDrawAmount != null) {
    return `${formatCurrency(helocDrawAmount)} (Draw) / ${formatCurrency(totalLoanAmount)} (Line)`;
  }

  return formatCurrency(totalLoanAmount);
};

export const getMonthlyPaymentValue = metrics => {
  const {
    interestOnly,
    interestOnlyPayment,
    principalAndInterest,
    hasRepaymentPeriod,
    monthlyPayment,
    drawBasedMonthlyPayment,
    qualifyingMonthlyPayment,
    loanTypeValue,
  } = metrics ?? {};

  const isHeloc = checkIsHelocLoanType(loanTypeValue);

  if (isHeloc && drawBasedMonthlyPayment != null && monthlyPayment != null) {
    return `${formatCurrency(drawBasedMonthlyPayment)} (Current) / ${formatCurrency(
      qualifyingMonthlyPayment ?? monthlyPayment,
    )} (Qualifying)`;
  }

  const hasDistinctIoPayment =
    interestOnly && interestOnlyPayment != null && interestOnlyPayment !== principalAndInterest;

  if (!hasDistinctIoPayment) {
    return formatCurrency(
      interestOnly ? (interestOnlyPayment ?? principalAndInterest) : principalAndInterest,
    );
  }

  if (!hasRepaymentPeriod) {
    return formatCurrency(interestOnlyPayment);
  }

  return `${formatCurrency(interestOnlyPayment)} (I/O) / ${formatCurrency(
    principalAndInterest,
  )} (P&I)`;
};

// Chattel loans have no Property Expenses card (PropertyAndLoanSummary is real-estate only), so the
// PITI is surfaced here next to the P&I. A not-yet-priced loan (PITI null/0) keeps the P&I-only value.
export const getChattelMonthlyPaymentValue = metrics => {
  const { principalAndInterest, piti } = metrics ?? {};
  const hasDistinctPiti = _.isNumber(piti) && piti > 0 && piti !== principalAndInterest;

  if (!hasDistinctPiti) {
    return getMonthlyPaymentValue(metrics);
  }

  return `${formatCurrency(principalAndInterest)} (P&I) / ${formatCurrency(piti)} (PITI)`;
};

// Mirrors Pricing's chattel housing expense for DTI (GetChattelHousingExpenses in
// LoanRatiosCalculator): PITI plus the monthly site expenses (lot rent + land payment). PITI itself
// stays site-expense free so it keeps matching dbo.Loans.Piti, the forms and the 1003.
export const getChattelHousingExpense = ({ piti, chattelDetails }) => {
  const pitiAmount = Number(piti ?? 0);
  const monthlyLotRent = Number(chattelDetails?.monthlyLotRent ?? 0);
  const landMonthlyPayment = Number(chattelDetails?.landMonthlyPayment ?? 0);
  const siteExpenses = _.round(monthlyLotRent + landMonthlyPayment, DEFAULT_DECIMAL_SCALE);

  return {
    piti: pitiAmount,
    monthlyLotRent,
    landMonthlyPayment,
    siteExpenses,
    housingExpense: _.round(pitiAmount + siteExpenses, DEFAULT_DECIMAL_SCALE),
  };
};

// A card shown from several preview DTOs is Direct when any of its fields is, otherwise Recalculated when any is.
export const getPendingChangeSourcesChangeKind = (sources = []) =>
  getCombinedPendingChangeKind(
    sources.map(source => getModifiedFieldChangeKind(source, source.fieldNames)),
  );

// A card highlighted from several preview DTOs shows the authors behind each of their fields, one dot per author.
export const getPendingChangeSourcesAuthors = (getModifiedFieldAuthors, sources = []) => {
  const authors = _.uniqBy(
    sources.flatMap(
      ({ modifiedFieldDetails, fieldNames }) =>
        getModifiedFieldAuthors(modifiedFieldDetails, fieldNames) ?? [],
    ),
    'key',
  );

  return _.isEmpty(authors) ? null : authors;
};
